import * as React from 'react';
import { DamAccountLink } from './DamAccountLink';

const IFB_HOSTING_DOMAIN_URL = process.env.FIREBASE_IFB_DOMAIN_URL;
export const WEBFORM_LINE_PREFIX = 'Webform:';
export const SOURCE_ACCOUNT_LINE_PREFIX = 'Source Account:';
export const DESTINATION_ACCOUNT_LINE_PREFIX = 'Destination Account:';

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export type DescriptionTextPart =
  | { type: 'text'; value: string }
  | { type: 'accountId'; value: string };

/** Split description text so standalone account IDs can be rendered as links. */
export const splitTextByAccountIds = (
  text: string,
  accountIds: Array<string | number | null | undefined>
): DescriptionTextPart[] => {
  const uniqueIds = [
    ...new Set(
      accountIds
        .filter((id) => id != null && String(id).trim() !== '')
        .map((id) => String(id).trim())
    ),
  ].sort((a, b) => b.length - a.length);

  if (uniqueIds.length === 0) {
    return [{ type: 'text', value: text }];
  }

  const pattern = new RegExp(
    `(?<!\\d)(${uniqueIds.map(escapeRegExp).join('|')})(?!\\d)`,
    'g'
  );
  const parts: DescriptionTextPart[] = [];
  let lastIndex = 0;
  let match = pattern.exec(text);

  while (match) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', value: text.slice(lastIndex, match.index) });
    }
    parts.push({ type: 'accountId', value: match[1] });
    lastIndex = match.index + match[0].length;
    match = pattern.exec(text);
  }

  if (lastIndex < text.length) {
    parts.push({ type: 'text', value: text.slice(lastIndex) });
  }

  return parts.length > 0 ? parts : [{ type: 'text', value: text }];
};

const renderLineWithAccountIds = (
  line: string,
  accountId: string | number | null | undefined
): React.ReactNode =>
  splitTextByAccountIds(line, [accountId]).map((part, partIndex) =>
    part.type === 'accountId' ? (
      <DamAccountLink key={`account-${partIndex}`} ddAccountId={part.value}>
        {part.value}
      </DamAccountLink>
    ) : (
      <React.Fragment key={`text-${partIndex}`}>{part.value}</React.Fragment>
    )
  );

export const renderWorkflowDescription = (
  description: string | undefined,
  options?: {
    formId?: string;
    sourceAccountId?: string | number | null;
    destinationAccountId?: string | number | null;
  }
) => {
  if (!description) {
    return null;
  }

  const formId = options?.formId;
  const sourceAccountId = options?.sourceAccountId;
  const destinationAccountId = options?.destinationAccountId;

  return description.split('\n').map((line, index) => {
    if (
      line.startsWith(WEBFORM_LINE_PREFIX) &&
      formId &&
      IFB_HOSTING_DOMAIN_URL
    ) {
      const formTitle = line
        .slice(WEBFORM_LINE_PREFIX.length)
        .trim()
        .replace(/\]$/, '');
      return (
        <div key={index}>
          {WEBFORM_LINE_PREFIX}{' '}
          <a
            href={`${IFB_HOSTING_DOMAIN_URL}/builder/form/${formId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {formTitle || formId}
          </a>
        </div>
      );
    }

    if (line.startsWith(SOURCE_ACCOUNT_LINE_PREFIX)) {
      return (
        <div key={index}>{renderLineWithAccountIds(line, sourceAccountId)}</div>
      );
    }

    if (line.startsWith(DESTINATION_ACCOUNT_LINE_PREFIX)) {
      return (
        <div key={index}>
          {renderLineWithAccountIds(line, destinationAccountId)}
        </div>
      );
    }

    return <div key={index}>{line}</div>;
  });
};
